import React, { useRef, useEffect } from "react";
import A11yDialog from "a11y-dialog";
import styled from "styled-components";
import ReactDOM from "react-dom";
import { scaleUp } from "./Styles";

const Container = styled.div<{
  open: boolean;
  disableAnimation: boolean;
}>`
  visibility: ${({ open }) => (open ? "visible" : "hidden")};
  z-index: 1000;
  // Position fixed required for mobile. Absolute interacts poorly with mobile header/footer
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  ${({ open, disableAnimation }) => (!disableAnimation ? scaleUp(open) : "")};
`;

interface FullScreenDialogProps {
  open: boolean;
  label: string;
  children;
  className?: string;
  onRequestClose: () => void;
  disableAnimation?: boolean;
}

export default function FullScreenDialog({
  open,
  label,
  children,
  className = "",
  disableAnimation = false,
  onRequestClose,
}: FullScreenDialogProps) {
  const dialogContainer = useRef<HTMLDivElement>(null);
  const a11yDialog = useRef<A11yDialog>();
  const root = useRef(document.getElementById("root"));

  useEffect(() => {
    // Make sure a11yDialog hasn't already been initialize. This code could run again if onRequestClose changes its reference
    if (!a11yDialog.current) {
      a11yDialog.current = new A11yDialog(
        dialogContainer.current,
        root.current
      );
      a11yDialog.current?.on("hide", () => {
        onRequestClose?.();
      });
    }
  }, [dialogContainer, onRequestClose]);

  useEffect(() => {
    if (open) {
      a11yDialog.current?.show();
    } else {
      a11yDialog.current?.hide();
    }
  }, [open, a11yDialog]);

  return ReactDOM.createPortal(
    <Container
      open={open}
      disableAnimation={disableAnimation}
      aria-label={label}
      role="dialog"
      ref={dialogContainer}
      className={className}
    >
      {open && children}
    </Container>,
    document.body
  );
}
